export interface TelemetryData {
  riderId: string;
  timestamp: number;
  latitude: number;
  longitude: number;
  altitude: number;
  speed: number;
  heartRate: number;
  power: number;
  cadence: number;
  distance: number;
  elapsedTime: number;
  gpsAccuracy: number;
  batteryLevel: number;
}

export interface CourseElevation {
  gain: number;
  loss: number;
  min: number;
  max: number;
}

export interface Course {
  coordinates: [number, number][]; // [lng, lat][]
  distance: number; // km
  elevation: CourseElevation;
  name?: string;
}

export interface RoutePreview {
  coordinates: [number, number][]; // [lng, lat][] - downsampled 50-100 points
  bounds: {
    minLng: number;
    maxLng: number;
    minLat: number;
    maxLat: number;
  };
}

export interface Rider {
  id: string;
  nickname: string;
  color: string;
  deviceId?: string;
  status: 'connected' | 'disconnected';
  lastSeenAt?: string;
  ftp?: number; // Functional Threshold Power (W)
  maxHeartRate?: number; // Maximum Heart Rate (bpm)
}

// Pre-computed zone counts for O(1) access
// Power zones: 7 zones (Z1-Z7)
// HR zones: 5 zones (Z1-Z5)
export interface ZoneCounts {
  power: number[]; // [z1, z2, z3, z4, z5, z6, z7] counts
  hr: number[]; // [z1, z2, z3, z4, z5] counts
  totalPower: number; // total valid power samples
  totalHr: number; // total valid HR samples
}

export interface RiderWithTelemetry extends Rider {
  latestTelemetry?: TelemetryData;
  telemetryHistory: TelemetryData[];
  zoneCounts?: ZoneCounts; // Pre-computed zone distribution
}

export type EventType = 'live' | 'fit_replay';

export interface Event {
  id: string;
  name: string;
  code: string;
  status: 'pending' | 'active' | 'finished';
  type?: EventType;
  isPublic: boolean;
  isOpenContribution?: boolean;
  maxRiders: number;
  createdAt: string;
  startedAt?: number;
  finishedAt?: number;
  course?: Course | null;
  routePreview?: RoutePreview | null;
  totalDistance?: number | null; // km - 텔레메트리 기반 최대 거리
  locationName?: string | null; // Geocoded location name in English (e.g., "Seoul, South Korea")
  locationNameKo?: string | null; // Geocoded location name in Korean (e.g., "서울, 대한민국")
  startLatitude?: number | null;
  startLongitude?: number | null;
  riders?: Rider[];
  riderCount?: number;
  likeCount?: number;
  viewCount?: number;
  isLikedByCurrentUser?: boolean;
}

export interface EventLikeResponse {
  liked: boolean;
  likeCount: number;
}

export interface PopularEvent extends Event {
  likeCount: number;
  viewCount: number;
  isLikedByCurrentUser: boolean;
}

export interface RiderUpdatePayload {
  riderId: string;
  status: string;
  timestamp: number;
}

export interface EventUpdatePayload {
  eventId: string;
  status: string;
  timestamp: number;
}

export interface SpectatorCountPayload {
  eventId: string;
  count: number;
  timestamp: number;
}

// Badge Types
export const BadgeType = {
  BETA_TESTER: 'beta_tester',
} as const;

export type BadgeType = (typeof BadgeType)[keyof typeof BadgeType];

export interface UserBadge {
  type: BadgeType;
  name: string;
  description: string;
  icon: string;
  awardedAt: string;
}

export interface ChatMessage {
  id: string;
  eventId: string;
  userId?: string;
  nickname: string;
  profileImage?: string;
  badges?: BadgeType[];
  message: string;
  timestamp: number;
}

export interface User {
  id: string;
  email: string;
  nickname: string;
  profileImage?: string;
  provider: 'local' | 'google';
  role: 'user' | 'admin';
  createdAt: string;
  badges?: UserBadge[];
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

// Strava Types
export type TrainingDataSource = 'manual' | 'strava' | 'fit';

export interface StravaActivity {
  id: string;
  name: string;
  type: string;
  sport_type: string;
  start_date: string;
  start_date_local: string;
  timezone: string;
  distance: number; // meters
  moving_time: number; // seconds
  elapsed_time: number; // seconds
  total_elevation_gain: number; // meters
  average_speed: number; // m/s
  max_speed: number; // m/s
  average_heartrate?: number;
  max_heartrate?: number;
  average_watts?: number;
  max_watts?: number;
  weighted_average_watts?: number;
  kilojoules?: number;
  average_cadence?: number;
  has_heartrate: boolean;
  device_watts: boolean;
  start_latlng?: [number, number] | null; // [lat, lng]
  end_latlng?: [number, number] | null; // [lat, lng]
  map?: {
    id: string;
    summary_polyline?: string;
    polyline?: string;
  };
  // Added by our API (not from Strava)
  location_name?: string;
}

export interface TrainingSettings {
  ftp: number | null;
  ftpSource: TrainingDataSource | null;
  ftpUpdatedAt: string | null;
  maxHeartRate: number | null;
  maxHeartRateSource: TrainingDataSource | null;
  maxHeartRateUpdatedAt: string | null;
  stravaConnected: boolean;
  stravaAthleteName: string | null;
  stravaFtpNotificationEnabled: boolean;
}
